import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { ChartData } from '../../services/statistics';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface ChartCardProps {
  title: string;
  data: ChartData | null;
  type: 'bar' | 'doughnut' | 'line';
  loading?: boolean;
  height?: number;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  type,
  loading = false,
  height = 300,
  color = 'primary',
}) => {
  const getColorPalette = (color: string) => {
    switch (color) {
      case 'success':
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #f8fff8 100%)',
          border: '#4caf50',
          text: '#2e7d32',
        };
      case 'warning':
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #fffef8 100%)',
          border: '#ff9800',
          text: '#f57c00',
        };
      case 'error':
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #fff8f8 100%)',
          border: '#f44336',
          text: '#d32f2f',
        };
      case 'info':
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
          border: '#2196f3',
          text: '#1976d2',
        };
      case 'secondary':
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #fef8ff 100%)',
          border: '#9c27b0',
          text: '#7b1fa2',
        };
      default: // primary
        return {
          bg: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
          border: '#1976d2',
          text: '#0d47a1',
        };
    }
  };

  const colors = getColorPalette(color);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        cornerRadius: 8,
      },
    },
    scales: type !== 'doughnut' ? {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: colors.text,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0,0,0,0.1)',
        },
        ticks: {
          color: colors.text,
        },
      },
    } : undefined,
  };

  if (loading || !data) {
    return (
      <Card
        sx={{
          height: '100%',
          background: colors.bg,
          border: `1px solid ${colors.border}20`,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: colors.text,
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
          <Skeleton variant="rectangular" height={height - 80} sx={{ borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return <Bar data={data} options={chartOptions} />;
      case 'doughnut':
        return (
          <Doughnut
            data={data}
            options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  ...chartOptions.plugins.legend,
                  position: 'right' as const,
                },
              },
            }}
          />
        );
      case 'line':
        return <Line data={data} options={chartOptions} />;
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        background: colors.bg,
        border: `1px solid ${colors.border}20`,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: colors.text,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ height: height - 80, position: 'relative' }}>
          {renderChart()}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChartCard;

